<template>
  <div v-if="featureEnabled && (!showSnapshotText || currentAiSnapshot)" class="ai-scorecard-bar"
    :class="[`ai-scorecard-bar--${mode}`, {
      'ai-scorecard-bar--pinned': isPinned,
      'ai-scorecard-bar--with-away-message': hasAwayMessage,
      'ai-scorecard-bar--text': showSnapshotText
    }]">
    <p v-if="showSnapshotText" class="ai-scorecard-bar__snapshot" v-tooltip="{ content: currentAiSnapshot, theme: 'lookdeep-global' }">{{ currentAiSnapshot }}</p>
    <template v-else>
      <crystal-ball
        :patient-id="patientId"
        :patient-monitor="patientMonitor"
        :ai-snapshot="aiSnapshot"
        :ai-snapshot-updated-at="aiSnapshotUpdatedAt" style="width: 24px;" />
      <span v-for="chip in chips" :key="chip.field.key"
        v-tooltip="{ content: chip.tooltip, placement: 'bottom' }"
        class="ai-scorecard-bar__chip"
        :class="`ai-scorecard-bar__chip--${chip.state}`">
        <i class="mdi" :class="chip.icon"></i>
      </span>
    </template>
  </div>
</template>

<script>
import { CHANNEL, SERVER_EVENT } from '@/lib/Pusher'
import { resolveAiScorecard } from '@/lib/PatientUtils'
import CrystalBall from '@/components/CrystalBall.vue'

export default {
  name: 'AiScorecardBar',
  components: { CrystalBall },
  props: {
    scorecard: { type: Array, default: () => [] },
    aiSnapshot: { type: String, default: null },
    aiSnapshotUpdatedAt: { type: String, default: null },
    patientId: { type: Number, required: true },
    patientMonitor: { type: Object, default: null },
    blindsClosed: { type: Boolean, default: false },
    hasAwayMessage: { type: Boolean, default: false },
    mode: {
      type: String,
      default: 'video-overlay',
      validator: v => ['video-overlay', 'modal', 'inline'].includes(v)
    }
  },
  data() {
    return {
      hospitalChannel: null,
      localScorecard: null,
      localAiSnapshot: null
    }
  },
  computed: {
    featureEnabled() {
      return this.$store.state.currentUser?.enabled_flags?.includes('ai_scorecard_display')
    },
    chips() {
      return resolveAiScorecard(this.localScorecard || this.scorecard).filter(r => r.state !== 'unknown')
    },
    isPinned() {
      return this.mode !== 'video-overlay' || this.blindsClosed
    },
    nurseStationEnabled() {
      return this.$store.state.CentralMonitoringStore?.nurseStationEnabled
    },
    showSnapshotText() {
      return this.mode === 'video-overlay' && this.blindsClosed && !this.nurseStationEnabled
    },
    currentAiSnapshot() {
      return this.localAiSnapshot || this.aiSnapshot
    }
  },
  methods: {
    handleAiSnapshotUpdated(data) {
      if (data.patient_id === this.patientId) {
        this.localScorecard = data.ai_snapshot_scorecard
        this.localAiSnapshot = data.ai_snapshot
      }
    }
  },
  mounted() {
    this.hospitalChannel = this.$pusher.subscribe(CHANNEL.FOR_HOSPITAL({
      env: import.meta.env.VITE_ENVIRONMENT_NAME,
      hospital: this.$store.state.currentHospital
    }))
    this.hospitalChannel.bind(SERVER_EVENT.AI_SNAPSHOT_UPDATED, this.handleAiSnapshotUpdated)
  },
  beforeUnmount() {
    this.hospitalChannel?.unbind(SERVER_EVENT.AI_SNAPSHOT_UPDATED, this.handleAiSnapshotUpdated)
  }
}
</script>

<style scoped lang="scss">
.ai-scorecard-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 6px 12px;
  background: rgb(0 0 0 / 36%);
  color: var(--ld-text-inverse);
  border-radius: 4px;
  font-size: 16px;
  line-height: 1;
  pointer-events: auto;
}

.ai-scorecard-bar--video-overlay {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  z-index: 20;
  opacity: 0;
  transition: opacity 0.1s linear, top 0.1s linear;
}

.ai-scorecard-bar--pinned {
  opacity: 1;
}

.ai-scorecard-bar--video-overlay.ai-scorecard-bar--with-away-message {
  top: 26px;
}

.ai-scorecard-bar--modal {
  position: static;
  justify-content: center;
  margin: 0 0 12px 0;
  background: var(--ld-bg-tertiary);
  border-radius: 0;
  color: inherit;
}

.ai-scorecard-bar--inline {
  position: static;
  padding: 4px 8px;
  gap: 8px;
  background: transparent;
  color: inherit;
  font-size: 14px;
}

.ai-scorecard-bar__chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;

  i {
    font-size: 26px;
  }
}

.ai-scorecard-bar__chip--active {
  color: var(--ld-status-success);
}
.ai-scorecard-bar__chip--inactive {
  color: var(--ld-text-secondary);
}

.ai-scorecard-bar--text {
  justify-content: flex-start;
  text-align: left;
}

.ai-scorecard-bar__snapshot {
  margin: 0;
  padding-right: 20px;
  font-size: 13px;
  line-height: 1.3;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
